import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { adminApi, errText } from "./adminApi";

const BLANK = { title: "", start_date: "", end_date: "", prize_note: "", winner_count: 1, published: false };

export default function AdminChallenges() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/challenges");
    setData(data);
  }, []);
  useEffect(() => { load().catch(() => setError("Could not load challenges. Click Refresh.")); }, [load]);

  const openEditor = (c) => {
    setEditing(c?.week_id || "new");
    setForm(c ? { title: c.title, start_date: c.start_date, end_date: c.end_date, prize_note: c.prize_note, winner_count: c.winner_count, published: c.published } : BLANK);
    setError("");
  };
  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setError("");
    try {
      if (editing === "new") await adminApi.post("/admin/challenges", form);
      else await adminApi.put(`/admin/challenges/${editing}`, form);
      setNotice("Challenge saved.");
      setEditing(null);
      await load();
    } catch (err) { setError(errText(err)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}" permanently?`)) return;
    setError("");
    try { await adminApi.delete(`/admin/challenges/${c.week_id}`); await load(); }
    catch (err) { setError(errText(err)); }
  };

  return (
    <div data-testid="admin-challenges">
      <h4 style={{ marginBottom: 4 }}>Weekly Physics Challenge</h4>
      <span className="lead-sub">Paid monthly pass (₹99, editable in Pricing). One attempt per student per challenge. If you don&apos;t pick questions, 5 random active bank questions are used.</span>
      {error && <p className="admin-error" data-testid="challenge-error" style={{ marginTop: 12 }}>{error}</p>}
      {notice && <p className="tp-success-box" style={{ background: "#123a2e", border: "1px solid #12b76a", color: "#4ade80" }}>{notice}</p>}
      <div className="admin-row" style={{ margin: "14px 0" }}>
        <button className="admin-btn" onClick={() => openEditor(null)} data-testid="challenge-add"><Plus size={14} /> New weekly challenge</button>
      </div>
      {editing && <div className="admin-card" data-testid="challenge-editor">
        <div className="admin-row" style={{ justifyContent: "space-between" }}><h4>{editing === "new" ? "New challenge" : "Edit challenge"}</h4><button className="admin-btn" onClick={() => setEditing(null)}><X size={14} /></button></div>
        {error && <p className="admin-error">{error}</p>}
        <label className="admin-label">TITLE<input className="admin-input" value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="e.g. Week 3 Physics Sprint" data-testid="challenge-title" /></label>
        <div className="admin-row">
          <label className="admin-label">START DATE<input type="date" className="admin-input" value={form.start_date} onChange={(e) => update("start_date", e.target.value)} data-testid="challenge-start" /></label>
          <label className="admin-label">END DATE<input type="date" className="admin-input" value={form.end_date} onChange={(e) => update("end_date", e.target.value)} data-testid="challenge-end" /></label>
          <label className="admin-label">WINNERS<input className="admin-input" style={{ width: 90 }} value={form.winner_count} onChange={(e) => update("winner_count", Number(e.target.value) || 1)} data-testid="challenge-winners" /></label>
        </div>
        <label className="admin-label">PRIZE (PUBLISHED TO STUDENTS — only promise what is configured and budgeted)<input className="admin-input" value={form.prize_note} onChange={(e) => update("prize_note", e.target.value)} placeholder="e.g. Top scorer gets a ₹100 Amazon voucher" data-testid="challenge-prize" /></label>
        <p className="lead-sub" style={{ margin: "0 0 10px" }}>Tie-breaking (fixed, shown to students): total score → accuracy → fastest time → earliest final submission.</p>
        <label className="check-row"><input type="checkbox" checked={form.published} onChange={(e) => update("published", e.target.checked)} data-testid="challenge-published" /> Published (live during its dates)</label>
        <button className="admin-btn" onClick={save} data-testid="challenge-save">Save challenge</button>
      </div>}
      {data && data.challenges.map((c, i) => (
        <div className="admin-card" key={c.week_id} data-testid={`challenge-row-${i}`}>
          <div className="admin-row">
            <span className={c.published ? "status-pill approved" : "status-pill locked"}>{c.published ? "PUBLISHED" : "DRAFT"}</span>
            <span style={{ flex: 1, color: "#dbe6f4", fontSize: 13, fontWeight: 600 }}>{c.title} <span className="lead-sub">— {c.start_date} → {c.end_date} · {c.questions} questions · {c.attempts} attempts{c.prize_note ? ` · Prize: ${c.prize_note}` : ""}</span></span>
          </div>
          <div className="admin-row" style={{ marginTop: 10 }}>
            <button className="admin-btn" onClick={() => setExpanded(expanded === c.week_id ? null : c.week_id)} data-testid={`challenge-expand-${i}`}>{expanded === c.week_id ? "Hide questions" : "View questions"}</button>
            <button className="admin-btn" onClick={() => openEditor(c)} data-testid={`challenge-edit-${i}`}>Edit</button>
            <button className="admin-btn" onClick={() => remove(c)} data-testid={`challenge-delete-${i}`}><Trash2 size={13} /></button>
          </div>
          {expanded === c.week_id && <div style={{ marginTop: 10 }} data-testid={`challenge-questions-${i}`}>
            {(c.question_texts || []).map((q, j) => <p key={j} className="lead-sub" style={{ color: q.active ? "#9db2cc" : "#d92d20", margin: "4px 0" }}>Q{j + 1}. {q.text}{!q.active && " (inactive — will not be served)"}</p>)}
          </div>}
        </div>
      ))}
      {data && data.challenges.length === 0 && <div className="admin-empty">No challenges yet. Create one for this week — 5 questions will be auto-picked from the bank.</div>}
      {data && data.leaderboard.length > 0 && (
        <div className="admin-card" data-testid="challenge-admin-board">
          <h4>This month&apos;s leaderboard ({data.month})</h4>
          <div className="table-scroll"><table className="leads-table">
            <thead><tr><th>#</th><th>STUDENT</th><th>CONTACT</th><th>SCORE</th><th>ACCURACY</th><th>PLAYED</th></tr></thead>
            <tbody>{data.leaderboard.map((r) => (
              <tr key={r.student_id} data-testid={`challenge-board-${r.rank}`}>
                <td><b style={{ color: r.rank <= (data.challenges.find((c) => c.month === data.month)?.winner_count || 1) ? "#f77f00" : "inherit" }}>{r.rank}</b></td>
                <td><div className="lead-name">{r.name}</div><div className="lead-sub">{r.student_id}</div></td>
                <td className="lead-sub">{r.mobile}<br />{r.email}</td>
                <td><b>{r.score}/{r.max_score}</b></td>
                <td>{r.avg_accuracy}%</td>
                <td>{r.challenges_played}</td>
              </tr>
            ))}</tbody>
          </table></div>
          <p className="lead-sub">Winners per current prize rules are highlighted. Tie-breaking: score → accuracy → fastest time → earliest final submission.</p>
        </div>
      )}
    </div>
  );
}
